import { useEffect } from "react"
import styled from "styled-components"

import { useDebounceValue } from "usehooks-ts"

import { ParadiseShippingMethodCard } from "src/components/Paradise/ParadiseShipping/ParadiseShippingMethodChecker/ParadiseShippingMethodCard"
import { LoadingWrapper } from "src/components/Paradise/sharedStyles"
import { useFetchShippingMethods } from "src/data/ecommerce/ecommerceQueries"
import {
  TCountryCode,
  TCurrencyCode,
  TShippableProductInCart,
} from "src/data/ecommerce/ecommerceTypes"
import { MSkeleton } from "src/ui/MSkeleton/MSkeleton"
import { spacing } from "src/ui/spacing"

export function ParadiseAvailableShippingMethods({
  shippingCountry,
  currencyCode,
  shippableProducts,
}: {
  shippingCountry: TCountryCode
  currencyCode: TCurrencyCode
  shippableProducts: TShippableProductInCart[]
}) {
  const hasShippableProducts = shippableProducts.length > 0

  const [debouncedBody, setDebouncedBody] = useDebounceValue(
    {
      shippable_products: shippableProducts.map((product) => ({
        id: product.id,
        quantity: product.quantity,
      })),
      shipping_country: shippingCountry,
      currency: currencyCode,
    },
    1000
  )

  const fetchShippingMethods = useFetchShippingMethods({
    body: {
      ...debouncedBody,
      shippable_products: debouncedBody.shippable_products.filter(
        (product) => product.quantity > 0
      ),
    },
    options: {
      enabled: !!debouncedBody,
    },
  })

  useEffect(() => {
    if (!hasShippableProducts || !shippingCountry) {
      return
    }

    setDebouncedBody({
      shippable_products: shippableProducts.map((product) => ({
        id: product.id,
        quantity: product.quantity,
      })),
      // We ask for the country using a select, so we are more or less sure that it will be a valid country code
      shipping_country: shippingCountry as TCountryCode,
      currency: currencyCode,
    })
  }, [
    shippableProducts,
    shippingCountry,
    currencyCode,
    setDebouncedBody,
    hasShippableProducts,
  ])

  if (fetchShippingMethods.isInitialLoading) {
    return (
      <LoadingWrapper>
        <MSkeleton width="30ch" height={20} />
        <MSkeleton width="30ch" height={20} />
        <MSkeleton width="30ch" height={20} />
      </LoadingWrapper>
    )
  }

  return (
    <Wrapper>
      {fetchShippingMethods.data?.map((shippingMethod) => (
        <ParadiseShippingMethodCard
          key={shippingMethod.shipping_method_id}
          shippingMethod={shippingMethod}
        />
      ))}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
`
